/* SPEC-452 — nav ORGANISATION: the top-level sidebar grouping and the page-search index.
 *
 * WHY THIS FILE EXISTS. Sidebar.tsx owns PRESENTATION (status dot, alert badge — the
 * leading page icon went in SPEC-456, which made every nav row a plain single line).
 * It used to own the top-level grouping too, in the same `PAGE_META` record. Grouping is
 * not presentation — it is the nav's information architecture, it is the thing a planner
 * navigates by, and it is the thing a guard must be able to assert. So the group map and
 * the search index live here, in a pure module with no React and no bundler alias, and
 * `nav-groups.test.ts` asserts them under plain Node.
 *
 * THE GROUPING RULE (SPEC-452). Group by THE VERB THE USER IS PERFORMING, not by the
 * owning department and not by the data source:
 *
 *   Plan   decide what demand will be        (forecast, promo book, classification)
 *   Buy    decide what to bring in and hold  (requirements, targets, replenishment)
 *   Sell   understand what goes out          (sales, dormancy, market intelligence)
 *   Money  protect margin and cash           (supplier risk, retro billing, deductions)
 *   Trust  verify the system and its data    (docs, governance, reports, products, roadmap)
 *   Admin  configure the platform            (users/roles, platform analytics)
 *
 * Overview is deliberately OUTSIDE the groups — it is the landing surface, not a verb.
 *
 * ORDER IS EXPLICIT, AND THAT IS THE GUARD. Each group lists its page paths in render
 * order. The old code filtered the registry by group, so a page added to the registry
 * without a group entry silently VANISHED from the nav (SPEC-139 left a dev-only
 * console.warn for exactly this). Here the ordered lists are asserted against the
 * registry by nav-groups.test.ts, so an unlisted page fails CI; `FALLBACK_GROUP` keeps
 * the running app honest in the meantime by showing the page rather than hiding it.
 *
 * ACCESS IS NOT HERE. Role pages (auth.ROLE_PAGES) and the resolved workspace page list
 * decide what a user may SEE; this file only decides where a visible page SITS. */

export type NavGroupLabel = 'Plan' | 'Buy' | 'Sell' | 'Money' | 'Trust' | 'Admin';

/** The landing page — in no group. */
export const STANDALONE_PATH = '/';

/** Every page that sits in NO group, in order. SPEC-466: Alerts ("What needs a look") sits
 *  beside Overview — it is a cross-remit worklist, not one verb's page. SPEC-509: neither is
 *  a row in the sidebar list any more — Overview is the brand mark (rail + logo) and Alerts
 *  is in the rail's foot cluster — but both stay in page search and can be pinned. */
export const STANDALONE_PATHS: readonly string[] = [STANDALONE_PATH, '/alerts'];

/** Render order of the top-level groups. Favourites is prepended at runtime (per-user). */
export const NAV_GROUP_ORDER: NavGroupLabel[] = ['Plan', 'Buy', 'Sell', 'Money', 'Trust', 'Admin'];

/** Label of the per-user pinned group. Not in NAV_GROUP_ORDER — its membership is
 *  user state, not configuration, so it is assembled in the Sidebar at render time. */
export const FAVOURITES_GROUP = 'Favourites';

/** Where a registry page with no entry below is shown. Never hide a page: an
 *  unlisted page is a config omission, and hiding it is how Sudhin lost two
 *  screens in Sep 2026. CI catches the omission; the app shows the page. */
export const FALLBACK_GROUP: NavGroupLabel = 'Trust';

/** Page paths per group, in render order. Must cover every view-registry page
 *  except STANDALONE_PATH — nav-groups.test.ts asserts exactly that. */
export const NAV_GROUP_PAGES: Record<NavGroupLabel, string[]> = {
  // Deciding what demand will be. Demand Planning first — it is the daily surface.
  Plan: [
    '/demand-planning',
    '/forecast-workbench',
    '/box-a',
    '/promotions',
    '/regime-alerts',
    '/analogue-matching',
    '/forecast-benchmark',
    '/production-upload',
  ],
  // Deciding what to bring in and how much to hold. Supply Chain's own subtitle is
  // "What do I need to order, and what's stuck?" — the Buy verb, verbatim.
  Buy: [
    '/material-requirements',
    '/inventory-targets',
    '/replenishment-detail',
    '/supply-chain',
    '/routes',
  ],
  Sell: ['/sales', '/customer-dormancy', '/intelligence'],
  Money: ['/supplier-risk', '/retro-billing', '/deductions'],
  // SPEC-448: /methodology carries the policy, method, sign-off and audit tabs that came
  // off /governance. It sits beside it — both answer "can I trust this number".
  // SPEC-455 — /demand-source is a TRUST surface too: it verifies which demand number is live
  // and whether the two candidates agree. It decides nothing, so it is not a Plan page.
  // SPEC-503 — /docs explains how every screen works; reading it is how you come to trust one.
  // SPEC-535 (Param, 08 Oct): Docs first — "how it works" is where verifying starts, and the
  // rail's Trust button lands on it.
  // SPEC-541 — /recommendations: the record of every suggestion, what was done and what happened.
  // Verifying the platform's own advice is the Trust verb.
  Trust: ['/docs', '/governance', '/methodology', '/demand-source', '/recommendations', '/reports', '/products', '/roadmap'],
  Admin: ['/admin', '/super-admin'],
};

/** The group a page sits in; null for a standalone page. */
export function groupOf(path: string): NavGroupLabel | null {
  if (STANDALONE_PATHS.includes(path)) return null;
  for (const label of NAV_GROUP_ORDER) {
    if (NAV_GROUP_PAGES[label].includes(path)) return label;
  }
  return FALLBACK_GROUP;
}

/* ── Page search ───────────────────────────────────────────────────────────────
 * Type-ahead over page TITLES plus this synonym index: the words a planner actually
 * types are rarely the page title ("oos", "mrp", "amendments", "rebate"). Titles come
 * from the view registry, so they can never drift; only the synonyms live here.
 *
 * Keep synonyms SHORT and UNAMBIGUOUS — one synonym maps to exactly one page, which
 * nav-groups.test.ts enforces, so a new entry cannot quietly steal another page's term. */
export const PAGE_SYNONYMS: Record<string, string[]> = {
  '/': ['home', 'dashboard', 'start', 'today', 'priority actions', 'briefing'],
  '/alerts': ['agents', 'watchers', 'findings', 'needs a look', 'what needs a look'],
  '/demand-planning': [
    'amendments', 'wape', 'bias', 'classification', 'psr', 'psr console', 'po approval',
    'scenarios', 'stock cover', 'bbe', 'planner', 'morning view', 'demand requests', 'deficit',
  ],
  '/forecast-workbench': ['workbench', 'base forecast', 'uplift', 'override'],
  '/box-a': ['box a', 'book', 'promo book', 'command centre', 'commitment'],
  '/promotions': ['promo', 'promo calendar', 'deal', 'funding'],
  '/analogue-matching': ['analogue', 'analog', 'npd', 'new product', 'launch', 'look-alike'],
  '/regime-alerts': ['regime', 'pattern shift', 'decline watch', 'ramping', 'transition'],
  '/forecast-benchmark': ['benchmark', 'champion', 'model comparison'],
  '/demand-source': ['source of demand', 'which number', 'shadow',
    'comparison', 'agreed plan', 'filed forecast', 'box a everywhere'],
  '/recommendations': ['suggested vs actioned', 'actioned', 'outcomes', 'track record',
    'learning loop', 'did we act'],
  '/production-upload': ['mps', 'production plan', 'schedule upload'],
  '/material-requirements': ['mrp', 'bom', 'raw material', 'components', 'ingredients'],
  '/inventory-targets': ['safety stock', 'reorder point', 'cover target', 'service level', 'targets'],
  '/replenishment-detail': ['replenishment', 'reorder', 'buy signal', 'suggested order'],
  '/supply-chain': ['oos', 'out of stock', 'stockout', 'open po', 'overstock', 'shortage', 'buying window'],
  '/routes': ['route planning', 'delivery', 'logistics', 'dispatch', 'order pool', 'drops'],
  '/sales': ['revenue', 'orders', 'turnover', 'margin', 'customers'],
  '/customer-dormancy': ['dormant', 'lapsed', 'churn', 'win back'],
  '/intelligence': ['market', 'news', 'commodity', 'weather', 'fx', 'signals'],
  '/supplier-risk': ['supplier', 'vendor', 'concentration', 'lead time risk'],
  '/retro-billing': ['retro', 'rebate', 'retrospective', 'claims'],
  '/deductions': ['deduction', 'chargeback', 'short pay', 'invoice dispute'],
  // SPEC-448: the policy/method words moved with their tabs to /methodology. /governance
  // keeps the words a reader types when they want to know what the DATA is doing.
  '/governance': ['observability', 'lineage', 'data flow', 'pipeline', 'freshness', 'stale', 'coherence', 'provenance'],
  '/methodology': ['method', 'policy', 'rules', 'thresholds', 'constants', 'algorithms', 'guardrails', 'audit trail', 'changelog', 'phase sign-off'],
  '/reports': ['download', 'export', 'csv'],
  '/products': ['sku', 'product master', 'catalogue', 'catalog', 'item'],
  '/roadmap': ['milestones', 'releases', 'delivery plan'],
  '/docs': ['help', 'documentation', 'guide', 'how it works', 'manual'],
  '/admin': ['users', 'roles', 'permissions', 'accounts', 'workspaces'],
  '/super-admin': ['usage', 'telemetry', 'system analytics', 'platform health'],
};

/** Minimum shape searchPages needs — the view registry satisfies it. */
export interface SearchablePage {
  path: string;
  label: string;
}

/* ── Favourite entries (SPEC-452 / SPEC-456) ───────────────────────────────────
 * A stored favourite is EXACTLY one of two strings, and the backend validator in
 * backend/routes/workspaces.py accepts exactly the same two:
 *   "/governance"                     a whole page
 *   "/demand-planning?tab=forecast"   one sub-view of a page
 *
 * These two functions live HERE, not in Sidebar.tsx, for the same reason the grouping
 * does: they are rules a guard must be able to assert, and this module is pure (no
 * React, no `@/` alias) so nav-groups.test.ts can run them under plain Node. Parsing is
 * strict rather than tolerant — the backend never stores anything else, so an entry that
 * does not parse is corrupt state and is DROPPED, never guessed at. */
const FAV_PAGE_RE = /^\/[A-Za-z0-9_\-/]*$/;
const FAV_TAB_RE = /^(\/[A-Za-z0-9_\-/]*)\?tab=([A-Za-z0-9_-]+)$/;

export interface FavouriteTarget {
  path: string;
  /** Absent for a whole-page pin. */
  tabKey?: string;
}

/** The page (and sub-view) a stored favourite names; null if it is neither shape. */
export function parseFavourite(entry: string): FavouriteTarget | null {
  if (entry.includes('?')) {
    const m = FAV_TAB_RE.exec(entry);
    return m ? { path: m[1], tabKey: m[2] } : null;
  }
  return FAV_PAGE_RE.test(entry) ? { path: entry } : null;
}

/** SPEC-488 — where a suggestion was filed from, as structured fields (the BACKEND turns
 *  them into the sidebar words; there is deliberately no label logic here).
 *
 *  The tab is read through `parseFavourite` — the SPEC-456 parser — so a malformed `?tab=`
 *  reads as "no tab" rather than being passed on. The entity is only what the URL itself
 *  names (`/products/:code` or `?product_code=`): a selection held in component state is
 *  not visible from here, and an absent entity is sent as null, never guessed. */
export interface SuggestionContext {
  page: string;
  tab: string | null;
  url: string;
  context: { entity_type: string; entity_id: string } | null;
}

export function suggestionContext(pathname: string, search: string): SuggestionContext {
  const page = (pathname || '/').split('?')[0];
  const params = new URLSearchParams(search || '');
  const rawTab = params.get('tab');
  const tab = rawTab ? (parseFavourite(`${page}?tab=${rawTab}`)?.tabKey ?? null) : null;
  const nested = /^\/products\/([^/]+)$/.exec(page);
  let id: string | null = null;
  if (nested) {
    try { id = decodeURIComponent(nested[1]); } catch { id = nested[1]; }
  } else {
    id = params.get('product_code');
  }
  return {
    page,
    tab,
    url: `${page}${search || ''}`,
    context: id ? { entity_type: 'product', entity_id: id } : null,
  };
}

/** Minimum page shape favouriteLabel needs — the view registry satisfies it. */
export interface FavouritablePage extends SearchablePage {
  groups?: Array<{ tabs: Array<{ key: string; label: string }> }>;
}

/**
 * The one wording for a favourite: "Governance", or "Demand Planning · Forecast".
 *
 * Returns null when the pin must be SILENTLY OMITTED from the nav — the entry is
 * malformed, its page is absent from `pages`, or it names a sub-view that page does not
 * have or that this user/workspace may not see. `pages` is the caller's already
 * access-filtered list (same contract as searchPages), and `visibleTabKeys` is the tab
 * keys allowed for that page — undefined meaning the page has no sub-views at all, which
 * makes any tab-qualified pin on it invalid.
 */
export function favouriteLabel(
  entry: string,
  pages: FavouritablePage[],
  visibleTabKeys?: string[],
): string | null {
  const target = parseFavourite(entry);
  if (!target) return null;
  const page = pages.find((p) => p.path === target.path);
  if (!page) return null;
  if (!target.tabKey) return page.label;
  if (!visibleTabKeys?.includes(target.tabKey)) return null;
  for (const group of page.groups ?? []) {
    const tab = group.tabs.find((t) => t.key === target.tabKey);
    if (tab) return `${page.label} · ${tab.label}`;
  }
  return null;
}

/* Match tiers, best first. An EXACT synonym beats a partial title match on purpose:
 * "oos" must land on Supply Chain even though no title contains it, and "targets"
 * must land on Stock Targets rather than on whichever title happens to contain it. */
const NO_MATCH = 99;

function scorePage(page: SearchablePage, q: string, synonyms: string[]): number {
  const label = page.label.toLowerCase();
  if (label === q) return 0;
  if (synonyms.includes(q)) return 1;
  if (label.startsWith(q)) return 2;
  if (synonyms.some((s) => s.startsWith(q))) return 3;
  if (label.includes(q)) return 4;
  if (synonyms.some((s) => s.includes(q))) return 5;
  if (page.path.toLowerCase().includes(q)) return 6;
  return NO_MATCH;
}

/**
 * Pages matching `query`, best match first. Pure: `pages` is the caller's already
 * access-filtered list, so search can never surface a page the user may not see.
 * An empty or whitespace query returns no results (the nav shows its groups instead).
 */
export function searchPages<T extends SearchablePage>(query: string, pages: T[]): T[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const scored: Array<{ page: T; score: number; order: number }> = [];
  pages.forEach((page, order) => {
    const score = scorePage(page, q, PAGE_SYNONYMS[page.path] ?? []);
    if (score !== NO_MATCH) scored.push({ page, score, order });
  });
  // Ties keep the caller's order (registry order) — a stable, explainable list.
  scored.sort((a, b) => (a.score - b.score) || (a.order - b.order));
  return scored.map((s) => s.page);
}
